import { ICardListGroupConfig } from '~/client/src/shared/components/CardList/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { hierarchyOptionOrderNesting } from '~/client/src/shared/resources/grouping/hierarchyGrouping'
import { compareGroupsByLabel } from '~/client/src/shared/utils/compareGroupsByLabel'
import { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

import { IResolvedPickerField } from './useResourcePickerState'

export interface IPickerGroupBand {
  resourceTypeId: string
  label: string
  hierarchy?: IHierarchyAccessors
}

export interface IPickerGroupKey {
  resourceTypeId: string
  /** The field value; null for a resource band's own key. */
  value: string | null
}

export interface IPickerGrouping {
  config: ICardListGroupConfig<IResourceRecord>
  describeGroup: (key: string) => IPickerGroupKey
}

type GetResolvedField = (
  recordId: string,
  key: string,
) => IResolvedPickerField | undefined

interface IFieldGroupConfigOptions {
  groupByKey: string
  records: IResourceRecord[]
  getResolvedField: GetResolvedField
  hierarchy?: IHierarchyAccessors
}

function buildFieldGroupConfig({
  groupByKey,
  records,
  getResolvedField,
  hierarchy,
}: IFieldGroupConfigOptions): ICardListGroupConfig<IResourceRecord> {
  const labelsByValue = new Map<string, string>()

  if (!hierarchy) {
    records.forEach(record => {
      const resolved = getResolvedField(record.id, groupByKey)

      if (!resolved || resolved.groupEntries.length === 0) {
        labelsByValue.set('', Localization.translator.unassigned)
        return
      }

      resolved.groupEntries.forEach(entry => {
        labelsByValue.set(entry.value, entry.label)
      })
    })
  }

  return {
    groupBy: (record: IResourceRecord) => {
      const resolved = getResolvedField(record.id, groupByKey)

      if (!resolved || resolved.groupEntries.length === 0) {
        return ['']
      }

      return resolved.groupEntries.map(entry => entry.value)
    },
    getGroupLabel: (key: string) =>
      hierarchy
        ? key
          ? hierarchy.getOwnLabel(key)
          : Localization.translator.unassigned
        : labelsByValue.get(key) || Localization.translator.unassigned,
    // Hierarchy group-by nests along ancestry, in the property's option order.
    ...(hierarchy && hierarchyOptionOrderNesting(hierarchy)),
  }
}

// Group values are per-type option ids, so two types can share one; the key carries
// the type. A unit separator, which neither a type id nor an option id contains.
const BAND_SEPARATOR = '\u001f'

const toBandKey = (resourceTypeId: string, value: string) =>
  resourceTypeId + BAND_SEPARATOR + value

const parseBandKey = (key: string): IPickerGroupKey => {
  const index = key.indexOf(BAND_SEPARATOR)
  return index < 0
    ? { resourceTypeId: key, value: null }
    : { resourceTypeId: key.slice(0, index), value: key.slice(index + 1) }
}

interface IBuildPickerGroupConfigOptions {
  groupByKey: string
  records: IResourceRecord[]
  getResolvedField: GetResolvedField
  getRecordTypeId: (record: IResourceRecord) => string
  /** One per resource type, in order. */
  bands: IPickerGroupBand[]
}

// One type groups by the field. Several group Resource first: each type's band is a
// tree parent over that type's own field groups.
export function buildPickerGroupConfig({
  groupByKey,
  records,
  getResolvedField,
  getRecordTypeId,
  bands,
}: IBuildPickerGroupConfigOptions): IPickerGrouping {
  if (bands.length === 1) {
    const [{ resourceTypeId, hierarchy }] = bands
    return {
      config: buildFieldGroupConfig({
        groupByKey,
        records,
        getResolvedField,
        hierarchy,
      }),
      describeGroup: value => ({ resourceTypeId, value }),
    }
  }

  const recordsByType = new Map<string, IResourceRecord[]>()
  records.forEach(record => {
    const resourceTypeId = getRecordTypeId(record)
    const list = recordsByType.get(resourceTypeId)
    if (list) list.push(record)
    else recordsByType.set(resourceTypeId, [record])
  })

  const bandByType = new Map(
    bands.map((band, index) => [
      band.resourceTypeId,
      {
        band,
        index,
        config: buildFieldGroupConfig({
          groupByKey,
          records: recordsByType.get(band.resourceTypeId) ?? [],
          getResolvedField,
          hierarchy: band.hierarchy,
        }),
      },
    ]),
  )

  const ancestorsByKey = new Map<string, readonly string[]>()
  const getGroupAncestors = (key: string): readonly string[] => {
    if (!ancestorsByKey.has(key)) {
      const { resourceTypeId, value } = parseBandKey(key)
      const inner = bandByType.get(resourceTypeId).config
      ancestorsByKey.set(
        key,
        value === null
          ? []
          : [
              resourceTypeId,
              ...(inner.getGroupAncestors?.(value) ?? []).map(ancestor =>
                toBandKey(resourceTypeId, ancestor),
              ),
            ],
      )
    }
    return ancestorsByKey.get(key)
  }

  return {
    config: {
      groupBy: record => {
        const resourceTypeId = getRecordTypeId(record)
        const values = bandByType.get(resourceTypeId).config.groupBy(record)
        return (Array.isArray(values) ? values : [values]).map(value =>
          toBandKey(resourceTypeId, value),
        )
      },
      getGroupLabel: (key, count) => {
        const { resourceTypeId, value } = parseBandKey(key)
        const { band, config } = bandByType.get(resourceTypeId)
        return value === null ? band.label : config.getGroupLabel(value, count)
      },
      getGroupAncestors,
      sortGroups: (a, b) => {
        const groupA = parseBandKey(a)
        const groupB = parseBandKey(b)
        const bandA = bandByType.get(groupA.resourceTypeId)
        if (groupA.resourceTypeId !== groupB.resourceTypeId) {
          return bandA.index - bandByType.get(groupB.resourceTypeId).index
        }
        if (groupA.value === null) return -1
        if (groupB.value === null) return 1
        // Within a band, the order the field's own grouping gives on its own.
        return bandA.config.sortGroups
          ? bandA.config.sortGroups(groupA.value, groupB.value)
          : compareGroupsByLabel(groupA.value, groupB.value, value =>
              bandA.config.getGroupLabel(value, 0),
            )
      },
    },
    describeGroup: parseBandKey,
  }
}
